import {
  Directive,
  inject,
  input,
  effect,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { AUTH_SERVICE } from '@core/tokens/auth.tokens';

/**
 * Directiva estructural que renderiza contenido según permisos del usuario.
 *
 * Uso básico:
 * ```html
 * <button *appHasPermission="'users:write'">Editar Usuario</button>
 * ```
 *
 * Con else template:
 * ```html
 * <div *appHasPermission="'reports:view'; else noAccess">Contenido protegido</div>
 * <ng-template #noAccess>Sin acceso</ng-template>
 * ```
 */
@Directive({
  selector: '[appHasPermission]',
  standalone: true,
})
export class HasPermissionDirective {
  private readonly authService = inject(AUTH_SERVICE);
  private readonly templateRef = inject(TemplateRef<unknown>);
  private readonly viewContainer = inject(ViewContainerRef);

  readonly appHasPermission = input.required<string>();
  readonly appHasPermissionElse = input<TemplateRef<unknown> | null>(null);

  private hasView = false;

  constructor() {
    effect(() => {
      const permission = this.appHasPermission();
      const elseTemplate = this.appHasPermissionElse();
      const hasAccess = this.authService.hasPermission(permission);

      if (hasAccess && !this.hasView) {
        this.viewContainer.clear();
        this.viewContainer.createEmbeddedView(this.templateRef);
        this.hasView = true;
      } else if (!hasAccess) {
        this.viewContainer.clear();
        if (elseTemplate) {
          this.viewContainer.createEmbeddedView(elseTemplate);
        }
        this.hasView = false;
      }
    });
  }
}
